"use client"

import { useEffect, useRef, useState } from "react"
import { Check } from "lucide-react"
import { usePrefersReducedMotion } from "@/components/glass/glass-kit"
import { PaymentMethodLogo } from "@/components/landing/payment-method-logos"
import { POINTS_CONFIG } from "@/lib/rewards"

const RENT_AMOUNT = 600
/** 1 pt/$1 of rent — the base rate, which is what a rent payment actually
 *  awards today. The AUTO_PAY_BONUS doubling this used to apply is dormant:
 *  nothing sets the flag that triggers it (see docs/rewards-brief.md). */
const POINTS_EARNED = Math.floor(RENT_AMOUNT * POINTS_CONFIG.BASE_RATE)

/**
 * Barber "Use your credit card" demo — card-on-file booth rent pays out,
 * expense lands in the ledger, BOOTH points tick up on completion.
 */
export function CreditCardArt({ className = "" }: { className?: string }) {
  const reduced = usePrefersReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const [started, setStarted] = useState(reduced)
  const [paid, setPaid] = useState(reduced)
  const [showPoints, setShowPoints] = useState(reduced)
  const [progress, setProgress] = useState(reduced ? 100 : 8)

  useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return

    let payTimeout = 0
    let pointsTimeout = 0
    let frame = 0
    let startMs = 0

    const runProgress = (now: number) => {
      if (!startMs) startMs = now
      const t = Math.min((now - startMs) / 900, 1)
      const eased = 1 - (1 - t) ** 3
      setProgress(8 + eased * 92)
      if (t < 1) {
        frame = requestAnimationFrame(runProgress)
      } else {
        setPaid(true)
        pointsTimeout = window.setTimeout(() => setShowPoints(true), 280)
      }
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setStarted(true)
        payTimeout = window.setTimeout(() => {
          frame = requestAnimationFrame(runProgress)
        }, 420)
        io.disconnect()
      },
      { threshold: 0.35 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      if (payTimeout) window.clearTimeout(payTimeout)
      if (pointsTimeout) window.clearTimeout(pointsTimeout)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [reduced])

  return (
    <div ref={ref} className={`absolute inset-x-0 bottom-0 space-y-2 ${className}`}>
      <div
        className="rounded-[20px] p-3.5 sm:p-4"
        style={{
          background: "var(--pg-card)",
          border: "1px solid var(--pg-card-edge)",
          boxShadow: "inset 0 1px 0 var(--pg-gloss)",
        }}
      >
        {/* Mini card — card-on-file, not cash */}
        <div
          className="relative overflow-hidden rounded-[14px] px-3 py-2.5"
          style={{
            background: "linear-gradient(135deg, rgba(38,38,42,0.95) 0%, rgba(22,22,26,0.98) 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div
            className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-30"
            style={{ background: "radial-gradient(circle, rgba(190,40,45,0.55) 0%, transparent 70%)" }}
          />
          <div className="relative flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="h-5 w-7 shrink-0 rounded-[4px]"
                style={{ background: "linear-gradient(145deg, #d4af6a 0%, #b8924a 100%)" }}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="text-[8px] font-medium tracking-wide" style={{ color: "rgba(255,255,255,0.45)" }}>
                  Card on file
                </p>
                <p className="truncate text-[10px] font-semibold tabular-nums text-white/90">Visa ···· 4242</p>
              </div>
            </div>
            <PaymentMethodLogo kind="visa" variant="tile" className="opacity-90" />
          </div>
        </div>

        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-medium tracking-wide" style={{ color: "var(--pg-muted)" }}>
              Monthly Booth Rent
            </p>
            <p className="mt-0.5 truncate text-[12px] font-semibold tracking-tight" style={{ color: "var(--pg-fg)" }}>
              BOOTH 1 · Obra Shop
            </p>
          </div>
          <span
            className="inline-flex shrink-0 items-center rounded-full px-2 py-[3px] text-[8.5px] font-medium tabular-nums transition-all duration-500 ease-out"
            style={
              showPoints
                ? { background: "rgba(168,85,247,0.16)", color: "rgb(192,132,252)", opacity: 1, transform: "scale(1)" }
                : { background: "var(--pg-line)", color: "var(--pg-faint)", opacity: 0, transform: "scale(0.92)" }
            }
          >
            +{POINTS_EARNED.toLocaleString()} pts
          </span>
        </div>

        <div className="mt-2.5 flex items-baseline justify-between gap-2">
          <span className="text-[20px] font-semibold leading-none tracking-[-0.03em] tabular-nums">
            ${RENT_AMOUNT.toFixed(2)}
          </span>
          <span
            className="inline-flex items-center gap-1 text-[9px] font-semibold transition-colors duration-500"
            style={{ color: paid ? "rgb(90,214,124)" : "var(--pg-muted)" }}
          >
            {paid ? (
              <>
                <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
                Paid
              </>
            ) : started ? (
              "Charging card…"
            ) : (
              "Due today"
            )}
          </span>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[9px] sm:text-[10px]">
            <span style={{ color: "var(--pg-muted)" }}>{paid ? "Expense tracked" : "Pay with card"}</span>
            <span className="font-medium tabular-nums" style={{ color: "var(--pg-fg)", opacity: 0.85 }}>
              {paid ? "Sep 1 · Receipt saved" : "No cash needed"}
            </span>
          </div>
          <div className="relative h-1.5 w-full overflow-hidden rounded-full" style={{ background: "var(--pg-line)" }}>
            <div
              className="h-full rounded-full transition-[width] duration-150 ease-out"
              style={{
                width: `${progress}%`,
                background: paid ? "rgb(90,214,124)" : started ? "var(--pg-muted)" : "var(--pg-line)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
